import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  classroomContextId,
  withClassroomContext,
} from "@/components/workspace/navigation";
import { formatDuration } from "../spacesTimeUtils";
import {
  SpaceRecapHeader,
  SpaceRecapMetrics,
  SpaceRecapStatus,
} from "../components/SpaceRecapLayout";
import { useSpaceRecap } from "../hooks/useSpaceRecap";

type StudentRecap = {
  spaceId: string;
  spaceName: string;
  description: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number;
  timeSpentMs: number;
  lessons: {
    lessonId: number;
    lessonName: string;
    bites: { name: string }[];
  }[];
};

const biteCount = (count: number) =>
  `${count} ${count === 1 ? "bite" : "bites"}`;

export default function StudentRecapPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const classroomId = classroomContextId(location.pathname, location.search);
  const { status, data, retry } = useSpaceRecap<StudentRecap>(
    `/spaces/${spaceId}/student-recap`,
  );
  // Lessons start expanded; tracking the collapsed ones keeps the first
  // rendered recap expanded instead of flipping open one commit later.
  const [collapsedLessons, setCollapsedLessons] = useState<Set<number>>(
    new Set(),
  );
  const toggleLesson = (id: number) =>
    setCollapsedLessons((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <div className="et-space-recap">
      <button
        type="button"
        className="et-recap-back"
        onClick={() => navigate(withClassroomContext("/spaces", classroomId))}
      >
        <ChevronLeft size={16} />
        Back to Spaces
      </button>
      <SpaceRecapStatus status={status} onRetry={retry} />
      {status === "ready" && data && (
        <>
          <SpaceRecapHeader
            name={data.spaceName}
            startedAt={data.startedAt}
            onViewCanvas={() =>
              navigate(withClassroomContext(`/spaces/${spaceId}`, classroomId))
            }
          />
          {data.description && (
            <section className="et-recap-objective">
              <h2>Learning objective</h2>
              <p>{data.description}</p>
            </section>
          )}
          <SpaceRecapMetrics
            items={[
              { label: "Time spent", value: formatDuration(data.timeSpentMs) },
              { label: "Duration", value: formatDuration(data.durationMs) },
              {
                label: "Status",
                value: data.timeSpentMs > 0 ? "Participated" : "Not attended",
              },
            ]}
          />
          <section aria-labelledby="recap-lessons-heading">
            <div className="et-recap-section-heading">
              <h2 id="recap-lessons-heading">What we covered</h2>
            </div>
            <div className="et-recap-panel et-recap-lessons">
              {data.lessons.length === 0 ? (
                <p className="et-recap-empty">
                  No lesson bites were attached to this space.
                </p>
              ) : (
                data.lessons.map((lesson) => {
                  const expanded = !collapsedLessons.has(lesson.lessonId);
                  return (
                    <section key={lesson.lessonId}>
                      <button
                        type="button"
                        aria-expanded={expanded}
                        aria-label={`${lesson.lessonName}, ${biteCount(lesson.bites.length)}`}
                        aria-controls={`recap-lesson-${lesson.lessonId}`}
                        onClick={() => toggleLesson(lesson.lessonId)}
                      >
                        <ChevronRight
                          size={17}
                          style={{
                            transform: expanded ? "rotate(90deg)" : "none",
                          }}
                        />
                        <strong>{lesson.lessonName}</strong>
                        <span>{biteCount(lesson.bites.length)}</span>
                      </button>
                      <ul
                        id={`recap-lesson-${lesson.lessonId}`}
                        hidden={!expanded}
                      >
                        {lesson.bites.map((bite, index) => (
                          <li key={index}>{bite.name}</li>
                        ))}
                        {lesson.bites.length === 0 && (
                          <li>No bites attached to this lesson.</li>
                        )}
                      </ul>
                    </section>
                  );
                })
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
